import { useId, useState } from "react";
import {
  useBalanceSheet,
  useProfitLoss,
  useTrialBalance,
  type Group,
  type Period,
} from "@/api/books";
import { PeriodPicker, thisFy } from "@/components/books/PeriodPicker";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { SelectField } from "@/components/ui/field";
import { formatDate, formatInr } from "@/lib/format";

type Statement = "trial-balance" | "profit-loss" | "balance-sheet";
const STATEMENTS: { value: Statement; label: string }[] = [
  { value: "trial-balance", label: "Trial balance" },
  { value: "profit-loss", label: "Profit and loss" },
  { value: "balance-sheet", label: "Balance sheet" },
];

const TH = "px-4 py-2 font-medium";
const TD = "px-4 py-2";
const NUM = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div className="table-frame">
      <table className="data-table">{children}</table>
    </div>
  );
}

function Head({ columns }: { columns: { label: string; right?: boolean }[] }) {
  return (
    <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
      <tr>
        {columns.map((column) => (
          <th key={column.label} className={column.right ? `${TH} text-right` : TH}>
            {column.label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function GroupRows({ groups }: { groups: Group[] }) {
  return (
    <>
      {groups.map((group) => (
        <tbody key={group.group} className="border-b last:border-0">
          <tr className="bg-muted/30">
            <td className={`${TD} font-medium`} colSpan={2}>
              {group.group}
            </td>
            <td className={`${NUM} font-medium`}>{formatInr(group.total_inr)}</td>
          </tr>
          {group.lines.map((line) => (
            <tr key={`${group.group}:${line.account_id}:${line.name}`}>
              <td className={`${TD} text-muted-foreground`}>{line.code}</td>
              <td className={TD}>{line.name}</td>
              <td className={NUM}>{formatInr(line.amount_inr)}</td>
            </tr>
          ))}
        </tbody>
      ))}
    </>
  );
}

function TrialBalanceView({ period }: { period: Period }) {
  const report = useTrialBalance(period);
  if (!report.data) return <RequestError error={report.error} />;
  const { rows } = report.data;
  return (
    <Table>
      <Head
        columns={[
          { label: "Code" },
          { label: "Account" },
          { label: "Opening", right: true },
          { label: "Debits", right: true },
          { label: "Credits", right: true },
          { label: "Closing", right: true },
        ]}
      />
      <tbody>
        {rows.map((row) => (
          <tr key={row.account_id} className="border-b last:border-0">
            <td className={`${TD} text-muted-foreground`}>{row.code}</td>
            <td className={TD}>{row.name}</td>
            <td className={NUM}>{formatInr(row.opening_inr)}</td>
            <td className={NUM}>{formatInr(row.debit_inr)}</td>
            <td className={NUM}>{formatInr(row.credit_inr)}</td>
            <td className={NUM}>{formatInr(row.closing_inr)}</td>
          </tr>
        ))}
        <tr className="bg-muted/30 font-medium">
          <td className={TD} colSpan={2}>
            Total
          </td>
          <td className={NUM}>{formatInr(report.data.total_opening_inr)}</td>
          <td className={NUM}>{formatInr(report.data.total_debit_inr)}</td>
          <td className={NUM}>{formatInr(report.data.total_credit_inr)}</td>
          <td className={NUM}>{formatInr(report.data.total_closing_inr)}</td>
        </tr>
      </tbody>
    </Table>
  );
}

function ProfitLossView({ period }: { period: Period }) {
  const report = useProfitLoss(period);
  if (!report.data) return <RequestError error={report.error} />;
  const { tax } = report.data;
  return (
    <div className="flex flex-col gap-4">
      <Table>
        <Head columns={[{ label: "Code" }, { label: "Income" }, { label: "INR", right: true }]} />
        <GroupRows groups={report.data.income} />
        <tbody>
          <tr className="bg-muted/30 font-medium">
            <td className={TD} colSpan={2}>
              Total income
            </td>
            <td className={NUM}>{formatInr(report.data.total_income_inr)}</td>
          </tr>
        </tbody>
      </Table>
      <Table>
        <Head columns={[{ label: "Code" }, { label: "Expenses" }, { label: "INR", right: true }]} />
        <GroupRows groups={report.data.expenses} />
        <tbody>
          <tr className="bg-muted/30 font-medium">
            <td className={TD} colSpan={2}>
              Total expenses
            </td>
            <td className={NUM}>{formatInr(report.data.total_expenses_inr)}</td>
          </tr>
          <tr className="font-semibold text-primary">
            <td className={TD} colSpan={2}>
              Net profit / (loss)
            </td>
            <td className={NUM}>{formatInr(report.data.net_profit_inr)}</td>
          </tr>
        </tbody>
      </Table>
      <div className="rounded-lg border bg-card p-4 text-sm">
        <p className="font-medium">
          Income tax memo for FY {tax.fy_code}: {formatInr(tax.estimated_tax_inr)} at {tax.percent}%
        </p>
        <p className="text-muted-foreground">
          An estimate, not an entry. The CA posts the provision as a manual journal. Advance tax
          falls due in cumulative instalments:
        </p>
        <ul className="mt-2 grid gap-1 sm:grid-cols-4">
          {tax.schedule.map((row) => (
            <li key={row.due_on} className="rounded border px-2 py-1">
              {formatDate(row.due_on)} · {row.percent}% · {formatInr(row.cumulative_inr)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BalanceSheetView({ asOf }: { asOf: string }) {
  const report = useBalanceSheet(asOf);
  if (!report.data) return <RequestError error={report.error} />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Badge variant={report.data.balanced ? "success" : "destructive"}>
          {report.data.balanced ? "Balances" : "Does not balance"}
        </Badge>
        <span className="text-sm text-muted-foreground">as of {formatDate(report.data.as_of)}</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Table>
          <Head columns={[{ label: "Code" }, { label: "Assets" }, { label: "INR", right: true }]} />
          <GroupRows groups={report.data.assets} />
          <tbody>
            <tr className="bg-muted/30 font-semibold">
              <td className={TD} colSpan={2}>
                Total assets
              </td>
              <td className={NUM}>{formatInr(report.data.total_assets_inr)}</td>
            </tr>
          </tbody>
        </Table>
        <Table>
          <Head
            columns={[{ label: "Code" }, { label: "Equity and liabilities" }, { label: "INR", right: true }]}
          />
          <GroupRows groups={[...report.data.equity, ...report.data.liabilities]} />
          <tbody>
            <tr className="bg-muted/30 font-semibold">
              <td className={TD} colSpan={2}>
                Total equity and liabilities
              </td>
              <td className={NUM}>{formatInr(report.data.total_liabilities_and_equity_inr)}</td>
            </tr>
          </tbody>
        </Table>
      </div>
    </div>
  );
}

export function ReportsPanel() {
  const id = useId();
  const [statement, setStatement] = useState<Statement>("trial-balance");
  const [period, setPeriod] = useState<Period>(thisFy());
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          id={`${id}-statement`}
          label="Statement"
          value={statement}
          onChange={(v) => setStatement(v as Statement)}
          options={STATEMENTS}
        />
        <PeriodPicker value={period} onChange={setPeriod} asOfOnly={statement === "balance-sheet"} />
      </div>
      {statement === "trial-balance" && <TrialBalanceView period={period} />}
      {statement === "profit-loss" && <ProfitLossView period={period} />}
      {statement === "balance-sheet" && <BalanceSheetView asOf={period.to} />}
    </div>
  );
}
